import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  useTokens,
} from '@barracuda-internal/bds-core';
import { BACKEND_URL } from '../../../runtimeConfig';

const LoginComponent = ({ isLoading, error, onClearErrors }) => {
  const tokens = useTokens();
  const { search } = useLocation();

  useEffect(() => {
    return () => {
      if (onClearErrors) {
        onClearErrors();
      }
    };
  }, [onClearErrors]);

  // Session-expired banner (from axios 401 interceptor redirect)
  const isSessionExpired = new URLSearchParams(search).get('reason') === 'session_expired';

  // Extract error from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const urlError = urlParams.get('error');
  const displayError = error || urlError;

  const getErrorMessage = (errorCode) => {
    const errorMessages = {
      'missing_code': 'Authorization failed. Please try again.',
      'invalid_state': 'Session expired. Please try again.',
      'missing_verifier': 'Session expired. Please try again.',
      'missing_id_token': 'Authentication failed. Please try again.',
      'missing_email': 'Unable to retrieve your email. Please contact support.',
      'user_not_found': 'User not found. Please contact your administrator.',
      'authentication_failed': 'Authentication failed. Please try again.',
      'account_expired': 'Your account has expired. Please contact your administrator.',
    };
    return errorMessages[errorCode] || errorCode || 'An error occurred. Please try again.';
  };

  const handleLogin = () => {
    // Navigate to the backend host directly. Django uses request.get_host()
    // to select the redirect_uri sent to the IdP; going through the UI nginx
    // proxy loses that signal.
    const nextUrl = encodeURIComponent(window.location.pathname === '/login' ? '/' : window.location.pathname);
    window.location.href = `${BACKEND_URL}/api/v1/auth/login/?next=${nextUrl}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.aliasesColorBgPageDefault,
      }}
    >
      <Card
        bdsType="staticSubtle"
        bdsBoxShadow="default"
        bdsPadding="spacious"
        sx={{
          maxWidth: 400,
          width: '100%',
          margin: tokens.aliasesSpaceContainerDefault,
        }}
      >
        <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
          {/* Logo / Header */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{ color: tokens.aliasesColorBrandPrimary }}
            >
              Prompt Security
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: tokens.aliasesColorTextStaticSubtle }}
            >
              Control Plane
            </Typography>
          </Box>

          {/* Session-expired banner */}
          {isSessionExpired && (
            <Alert bdsType="neutralInfo">
              Your session has expired. Please log in again.
            </Alert>
          )}

          {/* Error Alert */}
          {displayError && (
            <Alert bdsType="neutralError">
              {getErrorMessage(displayError)}
            </Alert>
          )}

          {/* Content */}
          <Stack
            sx={{
              alignItems: 'center',
              gap: tokens.aliasesSpaceContainerDefault,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: tokens.aliasesColorTextStaticEmphasis,
              }}
            >
              Sign in with your Barracuda Cloud Control account to access Prompt Security.
            </Typography>

            <Button
              bdsType="interactiveEmphasis"
              onClick={handleLogin}
              disabled={isLoading}
              sx={{ minWidth: 200 }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                'Sign In with Barracuda'
              )}
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Box>
  );
};

export default LoginComponent;
